/*
 * Form & Function film (sections/tcs-form-function.liquid).
 *
 * Two ways to move it on (data-playback):
 *
 * "play": the film plays through once when at least 60% of it is on screen. It rests on each beat for
 * data-holds milliseconds (so its words can be read), then plays on to the next, and stops on the last.
 * It pauses below 25% on screen and carries on when it's back. The control pauses, plays and, at the
 * end, plays it again. Only the forward copy loads.
 *
 * "scroll": the page scrolls natively through a tall section with a sticky stage. Scroll position only
 * chooses a beat (data-zones are the points, as a fraction of the section, where each next beat starts).
 * The film then plays itself to that beat's resting frame (data-rests) at its own 24 fps:
 * - forwards from the forward video;
 * - backwards from a reversed copy, swapped in at the mirrored time.
 *
 * At rest the two copies show the same frame, so the swap can't be seen. A change of beat mid-play
 * turns the film towards the new rest without restarting it.
 *
 * Copy beats (data-ff-beat: fade-in start and end, fade-out start and end, in frames) and the
 * dimension line follow the frame on screen, so they run backwards too.
 *
 * Without videos, or when video won't play, the four resting stills crossfade instead. With reduced
 * motion or data saving, none of this runs and the section is the finished stand and its last beat.
 */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection && navigator.connection.saveData;

  function init(root) {
    if (root.dataset.ffReady) return;
    root.dataset.ffReady = '1';
    if (reduce || saveData) return;
    root.classList.add('is-live');   // the tall scroll section, set before anything loads so nothing shifts
    const playMode = root.dataset.playback === 'play';

    const FPS = Number(root.dataset.fps);
    const N = Number(root.dataset.frames);
    const rests = root.dataset.rests.split(',').map(Number);
    const zones = root.dataset.zones.split(',').map(Number);
    const dimsFrom = Number(root.dataset.dimsFrom);
    const stage = root.querySelector('.tcs-ff__stage');
    const films = root.querySelector('[data-ff-films]');
    const bar = root.querySelector('[data-ff-bar]');
    const beats = [...root.querySelectorAll('[data-ff-beat]')].map((el) => ({ el, t: el.dataset.ffBeat.split(',').map(Number) }));
    const dimEls = [...root.querySelectorAll('[data-ff-dims] line')];
    const [dimLine, dimL, dimR] = dimEls;
    const dimLabel = root.querySelector('[data-ff-dim-label]');
    const overlayEl = root.querySelector('[data-ff-overlay]');
    const overlays = overlayEl && overlayEl.textContent.trim() ? JSON.parse(overlayEl.textContent) : {};
    const next = root.querySelector('[data-ff-next]');

    const portrait = () => window.innerWidth / window.innerHeight < 0.9;
    const shape = portrait() ? 'mobile' : 'desktop';
    const overlay = overlays[shape] || {};

    // ---- stills: one per rest; the last is the section's own picture
    const stills = {};
    stills[rests[rests.length - 1]] = root.querySelector('[data-ff-still]');
    root.querySelectorAll('[data-ff-still-template]').forEach((t) => {
      const img = document.createElement('img');
      img.alt = '';
      img.decoding = 'async';
      img.src = t.dataset[shape];
      const wrap = document.createElement('div');
      wrap.className = 'tcs-ff__still';
      wrap.append(img);
      stills[Number(t.dataset.ffStillTemplate)] = wrap;
      films.before(wrap);
    });
    const showStill = (f) => Object.entries(stills).forEach(([k, el]) => el.classList.toggle('is-on', Number(k) === f));

    // ---- the frame on screen: copy, dimension line and progress follow it
    let frame = 0;
    function paint() {
      for (const b of beats) {
        const [a, c, d, e] = b.t;
        const v = frame < a || frame > e ? 0 : frame < c ? (frame - a) / (c - a) : frame <= d ? 1 : 1 - (frame - d) / (e - d);
        b.el.style.opacity = v.toFixed(3);
        b.el.style.transform = `translateY(${((1 - v) * 12).toFixed(1)}px)`;
        b.el.classList.toggle('is-on', v > 0.5);
      }
      bar.style.transform = `scaleX(${(frame / (N - 1)).toFixed(4)})`;
      dims();
    }
    function dims() {
      const n = Math.round(frame);
      const o = overlay[n];
      const on = o && n >= dimsFrom - 6 ? Math.min(1, (n - dimsFrom + 6) / 6) : 0;
      dimEls.forEach((el) => (el.style.opacity = on * 0.75));
      dimLabel.style.opacity = on;
      if (!on) return;
      // the films cover the stage (object-fit: cover): map the frame's 0..1 coordinates onto the screen
      const vw = stage.clientWidth, vh = stage.clientHeight;
      const fw = shape === 'mobile' ? 9 : 16, fh = shape === 'mobile' ? 16 : 9;
      const s = Math.max(vw / fw, vh / fh);
      const w = fw * s, h = fh * s, x0 = (vw - w) / 2, y0 = (vh - h) / 2;
      const lx = x0 + o[1] * w, ly = y0 + o[2] * h, rx = x0 + o[3] * w, ry = y0 + o[4] * h;
      const y = Math.min(ly, ry) - 18;
      const set = (el, a) => Object.entries(a).forEach(([k, v]) => el.setAttribute(k, v.toFixed(1)));
      set(dimLine, { x1: lx, y1: y, x2: rx, y2: y });
      set(dimL, { x1: lx, y1: y - 5, x2: lx, y2: y + 5 });
      set(dimR, { x1: rx, y1: y - 5, x2: rx, y2: y + 5 });
      dimLabel.style.left = `${(lx + rx) / 2}px`;
      dimLabel.style.top = `${y - 6}px`;
      dimLabel.textContent = `${o[0].toLocaleString('en-GB')} mm`;
    }

    // ---- the films
    const src = { forward: root.dataset[`${shape}Forward`], reverse: root.dataset[`${shape}Reverse`] };
    const kinds = playMode ? ['forward'] : ['forward', 'reverse'];
    let mode = kinds.every((k) => src[k]) ? 'video' : 'stills';
    const v = {};
    let ready = false;
    if (mode === 'video') {
      for (const kind of kinds) {
        const el = document.createElement('video');
        el.muted = true;
        el.playsInline = true;
        el.preload = 'auto';
        el.setAttribute('muted', '');
        el.setAttribute('playsinline', '');
        el.src = src[kind];
        films.append(el);
        v[kind] = el;
      }
      // iPhones ignore preload: a video loads nothing until it's played. So each is primed with a silent
      // play and an immediate pause, which muted inline video is allowed to do. Where even that's refused
      // (Low Power Mode, data saving), the section stays on its resting stills.
      const prime = (el) => {
        const p = el.play();
        return (p && p.then ? p : Promise.resolve()).then(() => {
          el.pause();
          return el.readyState >= 2 ? null : new Promise((res) => el.addEventListener('loadeddata', res, { once: true }));
        });
      };
      Promise.all(kinds.map((k) => prime(v[k])))
        .then(() => { if (mode === 'video') { ready = true; settle(); if (playMode) update(); } })
        .catch(() => toStills());
      kinds.forEach((k) => v[k].addEventListener('error', () => toStills(), { once: true }));
    }
    function toStills() {
      if (mode === 'stills') return;
      mode = 'stills';
      Object.values(v).forEach((el) => { el.pause(); el.classList.remove('is-on'); });
      active = null;
      frame = rests[beat];
      showStill(frame);
      paint();
      if (playMode) { clearTimeout(holdTimer); holdTimer = 0; if (started) arrive(); else update(); }
    }

    // time in each copy of the frame on screen. A tenth of a frame in, so a seek never lands on a frame's
    // boundary, where a browser may show the one before.
    const tOf = (kind, f) => ((kind === 'forward' ? f : N - 1 - f) + 0.1) / FPS;
    const fOf = (kind, t) => { const k = t * FPS - 0.1; return kind === 'forward' ? k : N - 1 - k; };

    let beat = 0;          // the beat chosen by the scroll
    let active = null;     // the copy playing, or null at rest
    let raf = 0;

    function show(kind) {
      kinds.forEach((k) => v[k].classList.toggle('is-on', kind === k));
    }
    // at rest, both copies wait on the resting frame, so either direction starts at once
    function settle() {
      if (mode !== 'video' || !ready || active) return;
      for (const kind of kinds) {
        const t = tOf(kind, frame);
        if (Math.abs(v[kind].currentTime - t) > 0.5 / FPS) v[kind].currentTime = t;
      }
      if (!kinds.some((k) => v[k].classList.contains('is-on'))) show('forward');
      Object.values(stills).forEach((el) => el.classList.remove('is-on'));
    }

    function go(i) {
      beat = i;
      const to = rests[i];
      if (mode === 'stills' || !ready) {
        frame = to;
        showStill(to);
        paint();
        if (playMode) arrive();
        return;
      }
      const kind = to > frame ? 'forward' : to < frame ? 'reverse' : null;
      if (!kind) return;
      const el = v[kind];
      if (active !== kind) {
        // starting, or turning round mid-play: line this copy up with the frame on screen first
        if (active) v[active].pause();
        active = kind;
        const t = tOf(kind, frame);
        const start = () => {
          if (active !== kind) return;
          if (playMode && !running()) { show(kind); return; }   // paused while it lined up: resume() plays it
          show(kind);
          const p = el.play();
          if (p && p.catch) p.catch(() => toStills());
          loop();
        };
        if (Math.abs(el.currentTime - t) > 0.5 / FPS) {
          el.currentTime = t;
          el.addEventListener('seeked', start, { once: true });
        } else start();
      }
    }

    function loop() {
      cancelAnimationFrame(raf);
      const step = () => {
        if (!active) return;
        const el = v[active];
        frame = Math.max(0, Math.min(N - 1, fOf(active, el.currentTime)));
        const to = rests[beat];
        const arrived = active === 'forward' ? frame >= to - 0.5 : frame <= to + 0.5;
        const wrongWay = active === 'forward' ? to < frame - 0.5 : to > frame + 0.5;
        if (arrived || wrongWay || el.ended) {
          el.pause();
          if (arrived || el.ended) {
            frame = to;
            el.currentTime = tOf(active, to);
          }
          active = null;
          paint();
          if (wrongWay) { go(beat); return; }   // the scroll turned back: play the other way
          settle();
          if (playMode) arrive();
          return;
        }
        paint();
        raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }

    if (next) {
      next.addEventListener('click', (e) => {
        const after = root.closest('.shopify-section')?.nextElementSibling || root.nextElementSibling;
        if (!after) return;
        e.preventDefault();
        after.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }

    // ---- "play": the film moves itself on, resting on each beat
    const holds = (root.dataset.holds || '').split(',').map(Number);
    const control = root.querySelector('[data-ff-control]');
    let started = false, finished = false, userPaused = false, visible = false, holdTimer = 0;
    function running() { return started && !finished && !userPaused && visible; }
    // at a beat's rest: wait for its words to be read, then on to the next; the last beat is the end
    function arrive() {
      clearTimeout(holdTimer);
      holdTimer = 0;
      if (beat >= rests.length - 1) { finished = true; renderControl(); return; }
      holdTimer = setTimeout(() => { holdTimer = 0; if (running()) go(beat + 1); }, holds[beat] || 2500);
    }
    function suspend() {
      clearTimeout(holdTimer);
      holdTimer = 0;
      if (active) { v[active].pause(); cancelAnimationFrame(raf); }
    }
    function resume() {
      if (active) {
        const p = v[active].play();
        if (p && p.catch) p.catch(() => toStills());
        loop();
      } else if (!holdTimer) arrive();
    }
    function update() {
      if (!started) {
        if (visible && !userPaused && (ready || mode === 'stills')) { started = true; arrive(); }
      } else if (!finished) {
        running() ? resume() : suspend();
      }
      renderControl();
    }
    function renderControl() {
      if (!control) return;
      const state = finished ? 'replay' : userPaused ? 'play' : 'pause';
      control.dataset.state = state;
      control.setAttribute('aria-label', { replay: 'Play the film again', play: 'Play film', pause: 'Pause film' }[state]);
    }
    function replay() {
      suspend();
      active = null;
      finished = false;
      userPaused = false;
      beat = 0;
      frame = rests[0];
      if (mode === 'video' && ready) {
        v.forward.currentTime = tOf('forward', frame);
        show('forward');
      } else showStill(frame);
      paint();
      arrive();
      renderControl();
    }
    if (playMode) {
      beat = 0;
      frame = rests[0];
      showStill(frame);
      paint();
      if (control) {
        control.hidden = false;
        control.addEventListener('click', () => {
          if (finished) { replay(); return; }
          userPaused = !userPaused;
          update();
        });
      }
      new IntersectionObserver((entries) => {
        const r = entries[entries.length - 1].intersectionRatio;
        if (r >= 0.6) visible = true;
        else if (r < 0.25) visible = false;
        else return;
        update();
      }, { threshold: [0, 0.25, 0.6] }).observe(stage);
      return;
    }

    // ---- "scroll": scroll chooses the beat
    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const r = root.getBoundingClientRect();
        const span = r.height - window.innerHeight;
        const p = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
        const i = zones.filter((z) => p >= z).length;
        if (i !== beat) go(i);
      });
    }

    // arriving mid-section (a reload, a link back): start on that beat without playing up to it
    {
      const r = root.getBoundingClientRect();
      const span = r.height - window.innerHeight;
      const p = span > 0 ? Math.min(1, Math.max(0, -r.top / span)) : 0;
      beat = zones.filter((z) => p >= z).length;
      frame = rests[beat];
    }
    showStill(frame);
    paint();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => { paint(); onScroll(); });
  }

  const start = () => {
    document.querySelectorAll('[data-ff]').forEach((root) => {
      if (reduce || saveData) return;   // the static section: the finished stand and its last beat
      // The tall layout is set straight away. The films (about 6 MB) wait for the page and the hero to
      // load first: they start with the visitor's first scroll, or 4 s after the page has loaded,
      // then load once the section is within two screens.
      root.classList.add('is-live');
      let armed = false;
      const arm = () => {
        if (armed) return;
        armed = true;
        ['scroll', 'touchstart', 'keydown', 'wheel'].forEach((t) => window.removeEventListener(t, arm));
        new IntersectionObserver((entries, io) => {
          if (entries.some((e) => e.isIntersecting)) { io.disconnect(); init(root); }
        }, { rootMargin: '200% 0px' }).observe(root);
      };
      ['scroll', 'touchstart', 'keydown', 'wheel'].forEach((t) => window.addEventListener(t, arm, { passive: true }));
      const later = () => setTimeout(arm, 4000);
      if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
  document.addEventListener('shopify:section:load', (e) => {
    const root = e.target.querySelector('[data-ff]');
    if (root) init(root);
  });
})();
